import { isBrowser } from "../services/general"
import { parse, stringify } from "flatted/esm"

// Matches the empty shape of AppContext's initialState.medensure.healthSurvey
// - kept as a literal (rather than importing AppContext here) so this
// low-level storage helper doesn't depend on the app's context module.
const EMPTY_HEALTH_SURVEY = {
  height: "",
  weight: "",
  usualBP: "",
  maxicareCardNumber: "",
  smokingHistory: "",
  medicalHistory: [],
  familyMedicalHistory: [],
  maxicareOfferings: [],
  programServiceRating: { value: "", label: "" },
  otherMedicalHistory: "",
  otherFamilyMedicalHistory: "",
  otherMaxicareOfferings: "",
}

// The rest of the wizard state (names, addresses, selected medicines, etc.)
// is intentionally still persisted so a refresh mid-flow doesn't lose the
// user's progress - but health-survey answers, the prescription password,
// and bank details are PHI/financial data that shouldn't sit in plaintext
// sessionStorage. They're cleared from the persisted copy only; in-memory
// state for the current tab is untouched, so the active step still works.
const stripSensitiveFields = state => {
  const redacted = { ...state }

  if (redacted.medensure) {
    redacted.medensure = {
      ...redacted.medensure,
      prescriptionPassword: "",
      healthSurvey: EMPTY_HEALTH_SURVEY,
    }
  }

  if (redacted.flexmed) {
    redacted.flexmed = {
      ...redacted.flexmed,
      bank: "",
      bankAccountNumber: "",
    }
  }

  return redacted
}

const isHealthSurveyEmpty = healthSurvey => {
  if (!healthSurvey) return true

  const hasFilledTextField =
    !!healthSurvey.height ||
    !!healthSurvey.weight ||
    !!healthSurvey.usualBP ||
    !!healthSurvey.maxicareCardNumber ||
    !!healthSurvey.smokingHistory ||
    !!healthSurvey.otherMedicalHistory ||
    !!healthSurvey.otherFamilyMedicalHistory ||
    !!healthSurvey.otherMaxicareOfferings
  const hasFilledList =
    (healthSurvey.medicalHistory && healthSurvey.medicalHistory.length > 0) ||
    (healthSurvey.familyMedicalHistory &&
      healthSurvey.familyMedicalHistory.length > 0) ||
    (healthSurvey.maxicareOfferings && healthSurvey.maxicareOfferings.length > 0)
  const hasProgramServiceRating = !!healthSurvey.programServiceRating?.value

  return !hasFilledTextField && !hasFilledList && !hasProgramServiceRating
}

// sessionStorage intentionally redacts PHI/financial fields. When rehydrating,
// keep any still-present in-memory values so navigation within the same tab
// does not wipe data that was never meant to round-trip through storage.
export const restoreSensitiveFieldsFromMemory = (sessionState, memoryState) => {
  const restored = { ...sessionState }

  if (restored.flexmed && memoryState?.flexmed) {
    restored.flexmed = {
      ...restored.flexmed,
      bank: memoryState.flexmed.bank || restored.flexmed.bank,
      bankAccountNumber:
        memoryState.flexmed.bankAccountNumber ||
        restored.flexmed.bankAccountNumber,
    }
  }

  if (restored.medensure && memoryState?.medensure) {
    const memoryHealthSurvey = memoryState.medensure.healthSurvey
    const sessionHealthSurvey = restored.medensure.healthSurvey
    const keepMemoryHealthSurvey =
      !isHealthSurveyEmpty(memoryHealthSurvey) &&
      isHealthSurveyEmpty(sessionHealthSurvey)
    let healthSurvey = sessionHealthSurvey
    if (keepMemoryHealthSurvey) healthSurvey = memoryHealthSurvey

    restored.medensure = {
      ...restored.medensure,
      prescriptionPassword:
        memoryState.medensure.prescriptionPassword ||
        restored.medensure.prescriptionPassword,
      healthSurvey,
    }
  }

  return restored
}

export const getContextFromSession = () =>
  isBrowser() && sessionStorage.getItem("contextState")
    ? JSON.parse(sessionStorage.getItem("contextState"))
    : {}

export const saveContextToSession = state => {
  if (isBrowser())
    sessionStorage.setItem(
      "contextState",
      JSON.stringify(stripSensitiveFields(state))
    )
}

export const removeContextFromSession = () => {
  if (isBrowser()) sessionStorage.removeItem("contextState")
}

export const generateInitialValues = ({ fields }) => {
  let initialValues = {}

  for (let field of fields) {
    switch (field.type) {
      case "checkbox":
      case "multiselect":
        initialValues[field.name] = []
        break
      case "upload":
        initialValues[field.name] = {}
        break
      case "number":
        initialValues[field.name] = ""
        break

      case "schedule":
        initialValues[field.name] = {}
        for (let fieldName of field.fieldNames) {
          initialValues[field.name][fieldName] = ""
        }
        break

      case "select":
        initialValues[field.name] = {
          label: "",
          value: "",
        }
        break

      case "date":
        initialValues[field.name] = {
          month: {
            label: "",
            value: "",
          },
          date: {
            label: "",
            value: "",
          },
          year: "",
        }
        break

      case "address":
        let addressInitialValues = {}
        if (field.initialValues) {
          addressInitialValues = JSON.parse(field.initialValues)
        }
        initialValues[field.name] = {
          city: addressInitialValues.city || {
            label: "",
            value: "",
          },
          province: addressInitialValues.province || {
            label: "",
            value: "",
          },
          siteName: addressInitialValues.siteName || {
            label: "",
            value: "",
          },
          streetAddress: addressInitialValues.streetAddress || "",
          addressType: addressInitialValues.addressType || "",
          notes: "",
        }
        break

      default:
        initialValues[field.name] = ""
        break
    }
  }

  return initialValues
}

